import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { X, ExclamationTriangle, Calendar } from 'react-bootstrap-icons'
import axiosService from '../../../../utils/AxiosService'
import { toastSuccess, toastError } from '../../../FormComponents/Toasts'
import Textarea from '../../../FormComponents/Textarea'
import DraftUploader from '../../../FormComponents/DraftUploader'
import DraftStatusBadge from '../../../Shared/DraftStatusBadge'
import YesNoToggle from '../../../Shared/YesNoToggle'
import DraftAssetPreview from '../../../Shared/DraftAssetPreview'
import DraftShareVersion from '../../../../models/DraftShareVersion'
import DraftShareAsset from '../../../../models/DraftShareAsset'
import PreUploadConfirmModal from './PreUploadConfirmModal'
import InfluencerChecklistSection from './InfluencerChecklistSection'
import { uploadCopy } from '../../../../utils/actionDisplay'

// Shared by the influencer's zero-auth portal (default apiBase) and the
// PM's "submit on behalf" view, which passes its own campaign/action-scoped
// apiBase - every sub-path below is identical either way.
export default observer(function EditableSubmission({ action, apiBase = '/api/advertiser/draft_share', role = 'influencer' }) {
  const { action_draft: actionDraft } = action
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (actionDraft.workingVersion) return

    axiosService.post(`${apiBase}/draft_versions`, { action_id: action.id })
      .then(version => actionDraft.addVersion(new DraftShareVersion(version)))
  }, [action.id])

  const version = actionDraft.workingVersion
  const previousFeedback = actionDraft.latestSubmittedVersion?.feedback || []

  // When the PM is filling this in "on behalf" of the influencer, this
  // component is wrapped inside DraftReviewWindow, which already renders
  // its own editable Due Date card above - showing it a second time here
  // would just duplicate the exact same value.
  const showDueDate = role !== 'pm' && actionDraft.fieldEnabled('due_date') && actionDraft.due_date
  const showUpload = actionDraft.fieldEnabled('upload')
  const showCaption = actionDraft.fieldEnabled('caption')
  const showLinksNotes = actionDraft.fieldEnabled('links_notes')
  const groupUploadWithText = showUpload && (showCaption || showLinksNotes)
  const uploadInfo = uploadCopy(action.action_type)

  // Sequential step numbers, matching the numbered sections in the Figma
  // reference - computed from whichever fields are actually visible for
  // this Action/role so a hidden one never leaves a gap (e.g. "1, 3, 4").
  // Co-author is deliberately excluded - the reference never numbers it.
  const numberedFields = [
    ['due_date', showDueDate], ['upload', showUpload], ['caption', showCaption],
    ['links_notes', showLinksNotes], ['checklist', actionDraft.fieldEnabled('checklist')],
  ].filter(([, visible]) => visible).map(([key]) => key)
  const stepNumber = field => numberedFields.indexOf(field) + 1

  // Reconstruct through the model instead of Object.assign(version, updated)
  // directly - the server sends null for unset fields, which would bypass
  // the model's null-coalescing and leave React controlled inputs with a
  // null value.
  const applyServerVersion = (updated) => Object.assign(version, new DraftShareVersion(updated))

  const saveField = (field, value) => {
    axiosService.patch(`${apiBase}/draft_versions/${version.id}`, { draft_version: { [field]: value } })
      .then(applyServerVersion)
      .catch(({ data: { error } }) => toastError(error))
  }

  const onAssetUploaded = (responses) => {
    responses.forEach(asset => version.draft_assets.push(new DraftShareAsset(asset)))
  }

  const removeAsset = (asset) => {
    axiosService.delete(`${apiBase}/draft_versions/${version.id}/draft_assets/${asset.id}`)
      .then(() => { version.draft_assets = version.draft_assets.filter(a => a.id !== asset.id) })
      .catch(({ data: { error } }) => toastError(error))
  }

  const submit = () => {
    setShowConfirm(false)
    setSubmitting(true)
    axiosService.post(`${apiBase}/draft_versions/${version.id}/submit`)
      .then(updated => {
        applyServerVersion(updated)
        actionDraft.draft_status = 'submitted'
        toastSuccess('Submission sent')
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setSubmitting(false))
  }

  if (!version) return null

  return (
    <div className="flex flex-col items-center">
      {/* PM's "submit on behalf" view is wrapped inside DraftReviewWindow,
          which already shows its own (more complete - Action Settings, etc.)
          header for this exact action right above. The influencer's own
          zero-auth portal has no such wrapper, so it still needs this one. */}
      {role !== 'pm' && (
        <div className="my-5 p-3 rounded-lg bg-white dark:bg-rb-dark-mode flex justify-between items-center w-[650px] border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter">
          <h2 className="font-bold text-lg dark:text-white">{action.name}</h2>
          <DraftStatusBadge draftStatus={actionDraft.draft_status} role={role} />
        </div>
      )}

      {actionDraft.draft_status === 'revision_requested' && previousFeedback.length > 0 && (
        <div className="border-1 border-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 w-[650px] mb-4">
          <p className="font-bold text-orange-700 dark:text-orange-300 mb-1">Revision requested</p>
          {previousFeedback.map(f => <p key={f.id} className="text-sm dark:text-white">{f.body}</p>)}
        </div>
      )}

      {showDueDate && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode flex items-center justify-between">
          <p className="font-bold dark:text-white whitespace-nowrap">{stepNumber('due_date')}. Due Date</p>
          <p className="flex items-center gap-2 text-sm font-medium dark:text-white">
            <Calendar /> {new Date(actionDraft.due_date).toLocaleString()}
          </p>
        </div>
      )}

      {(showUpload || showCaption || showLinksNotes) && (
        <div className={`border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode ${groupUploadWithText ? 'grid grid-cols-2 gap-6' : 'flex flex-col gap-4'}`}>
          {showUpload && (
            <div>
              <p className="font-bold mb-2 dark:text-white">
                {stepNumber('upload')}. {uploadInfo.label} {uploadInfo.formatNote && <span className="font-normal text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{uploadInfo.formatNote}</span>}
              </p>
              {version.draft_assets.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {version.draft_assets.map(asset => (
                    <div key={asset.id} className="relative">
                      <DraftAssetPreview asset={asset} className="w-24 h-24 object-cover rounded-lg" />
                      <button onClick={() => removeAsset(asset)} className="absolute -top-1 -right-1 bg-white dark:bg-rb-dark-mode rounded-full">
                        <X className="text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="h-[120px]">
                <DraftUploader
                  url={`${apiBase}/draft_versions/${version.id}/draft_assets`}
                  onDocumentUpload={onAssetUploaded}
                  onDocumentUploadFailure={() => toastError('Upload failed')}
                  acceptedFileTypes={uploadInfo.acceptedFileTypes}
                  hintText={uploadInfo.hintText}
                />
              </div>
            </div>
          )}

          {(showCaption || showLinksNotes) && (
            <div className="flex flex-col gap-4">
              {showCaption && (
                <div>
                  <p className="font-bold mb-2 dark:text-white">{stepNumber('caption')}. Add Caption</p>
                  <Textarea
                    rows={3}
                    value={version.caption}
                    onChange={e => (version.caption = e.target.value)}
                    onBlur={() => saveField('caption', version.caption)}
                    className="rounded-lg px-4 w-full"
                    topMargin=""
                  />
                </div>
              )}

              {showLinksNotes && (
                <div>
                  <p className="font-bold mb-2 dark:text-white">{stepNumber('links_notes')}. Links / Notes</p>
                  <Textarea
                    rows={2}
                    value={version.notes}
                    onChange={e => (version.notes = e.target.value)}
                    onBlur={() => saveField('notes', version.notes)}
                    className="rounded-lg px-4 w-full"
                    topMargin=""
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {actionDraft.fieldEnabled('co_author') && (role === 'pm' || version.co_author_response != null) && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode flex items-center justify-between">
          <p className="font-bold dark:text-white">Co-author allowed</p>
          {role === 'pm' ? (
            <YesNoToggle
              value={version.co_author_response}
              onChange={value => { version.co_author_response = value; saveField('co_author_response', value) }}
            />
          ) : (
            <p className="text-sm font-medium dark:text-white">{version.co_author_response ? 'Yes' : 'No'}</p>
          )}
        </div>
      )}

      {actionDraft.fieldEnabled('checklist') && (
        <InfluencerChecklistSection actionDraft={actionDraft} workingVersion={version} apiBase={apiBase} role={role} heading={`${stepNumber('checklist')}. Checklist`} />
      )}

      <div className="w-[650px] mb-8 flex justify-end">
        <button
          disabled={submitting}
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-rb-green rounded-lg disabled:opacity-50"
        >
          <ExclamationTriangle /> Send for Approval
        </button>
      </div>

      <PreUploadConfirmModal isOpen={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={submit} />
    </div>
  )
})
